// Promise-based confirm/prompt primitive — the single replacement for the
// ~29 native window.confirm/window.prompt calls scattered across the app.
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Delete?', description: '…', destructive: true }))) return;
//
//   const prompt = usePrompt();
//   const note = await prompt({ title: 'Leave note', defaultValue: '' }); // string | null
//
// One <ConfirmProvider> mounts near the app root; both hooks share it.
import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './dialog';
import { Button } from './button';
import { Input } from './input';

export interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as a destructive action (red). */
  destructive?: boolean;
}

export interface PromptOptions extends ConfirmOptions {
  defaultValue?: string;
  placeholder?: string;
  /** HTML input type — 'text' by default. */
  inputType?: string;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
type PromptFn = (opts: PromptOptions) => Promise<string | null>;

interface ConfirmContextValue {
  confirm: ConfirmFn;
  prompt: PromptFn;
}

const ConfirmContext = React.createContext<ConfirmContextValue | null>(null);

interface DialogState {
  mode: 'confirm' | 'prompt';
  options: PromptOptions;
  resolve: (value: boolean | string | null) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<DialogState | null>(null);
  const [value, setValue] = React.useState('');

  const confirm = React.useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setValue('');
      setState({ mode: 'confirm', options, resolve: (v) => resolve(v as boolean) });
    });
  }, []);

  const prompt = React.useCallback<PromptFn>((options) => {
    return new Promise<string | null>((resolve) => {
      setValue(options.defaultValue ?? '');
      setState({ mode: 'prompt', options, resolve: (v) => resolve(v as string | null) });
    });
  }, []);

  const settle = React.useCallback(
    (result: boolean | string | null) => {
      state?.resolve(result);
      setState(null);
    },
    [state],
  );

  const handleCancel = React.useCallback(() => {
    settle(state?.mode === 'prompt' ? null : false);
  }, [settle, state]);

  const handleConfirm = React.useCallback(() => {
    settle(state?.mode === 'prompt' ? value : true);
  }, [settle, state, value]);

  const ctx = React.useMemo<ConfirmContextValue>(() => ({ confirm, prompt }), [confirm, prompt]);
  const opts = state?.options;

  return (
    <ConfirmContext.Provider value={ctx}>
      {children}
      <Dialog open={state !== null} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        {state && opts && (
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{opts.title}</DialogTitle>
              {opts.description && <DialogDescription>{opts.description}</DialogDescription>}
            </DialogHeader>

            {state.mode === 'prompt' && (
              <form
                onSubmit={(e) => { e.preventDefault(); handleConfirm(); }}
              >
                <Input
                  autoFocus
                  type={opts.inputType ?? 'text'}
                  value={value}
                  placeholder={opts.placeholder}
                  onChange={(e) => setValue(e.target.value)}
                />
              </form>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={handleCancel}>
                {opts.cancelLabel ?? 'Cancel'}
              </Button>
              <Button
                variant={opts.destructive ? 'destructive' : 'default'}
                onClick={handleConfirm}
              >
                {opts.confirmLabel ?? 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

function useConfirmContext(): ConfirmContextValue {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm/usePrompt must be used within a <ConfirmProvider>');
  }
  return ctx;
}

export function useConfirm(): ConfirmFn {
  return useConfirmContext().confirm;
}

export function usePrompt(): PromptFn {
  return useConfirmContext().prompt;
}
